import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { PermissionDirective, PermissionType } from '@qtek/libs/permissions';
import { TemplatesStore } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { ErrorComponent } from '@qtek/libs/ui';
import { DocumentTemplate, TemplateTypes } from '@qtek/shared/models';
import { Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { createTemplateDialogConfig } from '../create-template-dialog';
import { CreateTemplateDialogComponent } from '../create-template-dialog/create-template-dialog.component';

@Component({
  selector: 'qt-template-selector',
  templateUrl: './template-selector.component.html',
  styleUrls: ['./template-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TemplateSelectorComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslationCoreModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatOptionModule,
    FlexLayoutModule,
    ErrorComponent,
    PermissionDirective,
  ],
})
export class TemplateSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private destroy$ = new Subject<void>();
  private templatesStore = inject(TemplatesStore);
  private dialog = inject(MatDialog);
  private injector = inject(Injector);

  templateType = input.required<TemplateTypes>();
  label = input<string>('GNR_TEMPLATE');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  /** Flip true to eagerly reveal a required-and-empty state; manual entry must never set this. */
  revealRequired = input<boolean>(false);

  readonly PermissionType = PermissionType;
  readonly feature = signal<string>('stp/tpls');

  templateControl = new FormControl<DocumentTemplate | null>(null);
  private existingTemplates: string[] = [];

  // Effects run after ngOnInit, so required()'s validators are already applied here.
  private readonly revealRequiredEffect = effect(() => {
    if (this.revealRequired() && this.templateControl.invalid) {
      this.templateControl.markAsDirty();
      this.templateControl.markAsTouched();
    }
  });

  protected templates = computed(() =>
    this.templatesStore.selectableTemplatesByType(this.templateType())
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange = (value: DocumentTemplate | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouchedFn = () => {};
  private isDisabled = false;

  constructor() {
    effect(() => {
      this.existingTemplates = this.templates().map(template => template.name);
    });
  }

  ngOnInit(): void {
    if (this.required()) {
      this.templateControl.setValidators([Validators.required]);
      this.templateControl.updateValueAndValidity({ emitEvent: false });
    }

    this.templatesStore.connectSelectable({});
  }

  ngOnDestroy(): void {
    this.templatesStore.disconnectSelectable();
    this.destroy$.next();
    this.destroy$.complete();
  }

  compareTemplates(
    template1: DocumentTemplate | null,
    template2: DocumentTemplate | null
  ): boolean {
    return template1 && template2
      ? template1._id === template2._id
      : template1 === template2;
  }

  openCreateTemplateDialog(): void {
    const dialogConfig = {
      ...createTemplateDialogConfig,
      data: {
        existingTemplates: this.existingTemplates,
        templateType: this.templateType(),
      },
    };
    const dialogRef = this.dialog.open(
      CreateTemplateDialogComponent,
      dialogConfig
    );

    dialogRef
      .afterClosed()
      .pipe(
        filter(result => !!result),
        switchMap(result =>
          toObservable(this.templates, { injector: this.injector }).pipe(
            map(templates => templates.find(t => t._id === result._id)),
            filter(template => !!template),
            take(1)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(template => {
        this.setValue(template);
      });
  }

  getFormControl(): FormControl<DocumentTemplate | null> {
    return this.templateControl;
  }

  setRequired(required: boolean): void {
    if (required) {
      this.templateControl.setValidators([Validators.required]);
    } else {
      this.templateControl.clearValidators();
    }
    this.templateControl.updateValueAndValidity();
  }

  setValue(template: DocumentTemplate | null): void {
    this.templateControl.setValue(template, { emitEvent: false });
    this.onChange(template);
  }

  reset(): void {
    this.templateControl.reset();
    this.templateControl.markAsUntouched();
    this.templateControl.markAsPristine();
  }

  writeValue(value: DocumentTemplate | null): void {
    this.templateControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: DocumentTemplate | null) => void): void {
    this.onChange = fn;
    this.templateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        fn(value);
      });
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  onTouched(): void {
    // Blur only notifies the outer CVA control — mark this inner one too or it never shows red.
    this.templateControl.markAsDirty();
    this.templateControl.markAsTouched();
    this.onTouchedFn();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.templateControl.disable({ emitEvent: false });
    } else {
      this.templateControl.enable({ emitEvent: false });
    }
  }
}
